// pages/notifications/template/[slug].js
import axios from "axios";
import React, { useEffect, useState } from "react";
import { app_url } from "../../config";
import Error500 from "../../components/500/Error500";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { validateImage } from "../../utils/imageValidator";

const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB

const Spinner = ({ size = 16, color = "currentColor" }) => (
  <span
    className="d-inline-block"
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite",
      flexShrink: 0,
    }}
  />
);

const ViewTemplate = () => {
  const { id } = useParams();
  const [editing, setEditing] = useState({ title: false, body: false });
  const [template, setTemplate] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [imageValidating, setImageValidating] = useState(false);

  const [saving, setSaving] = useState({
    title: false,
    body: false,
    image: false,
    status: false,
  });

  const [details, setDetails] = useState({ title: "", body: "" });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState("");

  const token = JSON.parse(localStorage.getItem("adminEtijwaal"))?.token;
  const navigate = useNavigate();

  const fetchTemplate = () => {
    setIsLoading(true);
    axios
      .get(`${app_url}/api/notification/getFcmTemplateById?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const t = res.data.template[0];
        setTemplate(t);
        setDetails({ title: t?.title || "", body: t?.body || "" });
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response) {
          if (err.response.status === 400) {
            toast.error(err.response.data.message);
          } else if (err.response.status === 401) {
            navigate("/");
          } else if (err.response.status === 500) {
            toast.error(err.response.data.message);
          } else {
            toast.error("An unexpected error occurred.");
          }
        } else {
          setOnline(navigator.onLine);
          toast.error("Network error. Please check your internet connection.");
          setErrMsg("Network error. Please check your internet connection.");
        }
      });
  };

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateTemplate = (payload, savingKey, slug) => {
    setSaving((s) => ({ ...s, [savingKey]: true }));

    const formData = new FormData();
    formData.append("slug", slug);
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, value);
    }); // for status, payload is {} so this loop simply adds nothing extra

    return axios
      .post(
        `${app_url}/api/notification/updateFcmTemplateById?id=${template.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      )
      .then((res) => {
        toast.success(res.data.message);
        setTemplate((prev) => ({ ...prev, ...payload }));
        fetchTemplate();
        return true;
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 400) {
            toast.error(err.response.data.message);
          } else if (err.response.status === 401) {
            navigate("/");
          } else if (err.response.status === 500) {
            toast.error(err.response.data.message);
          } else {
            toast.error("An unexpected error occurred.");
          }
        } else {
          setOnline(navigator.onLine);
          toast.error("Network error. Please check your internet connection.");
          setErrMsg("Network error. Please check your internet connection.");
        }
        return false;
      })
      .finally(() => {
        setSaving((s) => ({ ...s, [savingKey]: false }));
      });
  };

  const handleStatusToggle = () => {
    // Only slug: "status" is sent — no status value/payload.
    updateTemplate({}, "status", "status");
  };

  const saveTitle = () => {
    if (!details.title.trim()) {
      toast.error("Title cannot be empty");
      return;
    }
    updateTemplate({ title: details.title }, "title", "title").then((ok) => {
      if (ok) setEditing((e) => ({ ...e, title: false }));
    });
  };

  const cancelTitle = () => {
    setDetails((d) => ({ ...d, title: template.title }));
    setEditing((e) => ({ ...e, title: false }));
  };

  const saveBody = () => {
    if (!details.body.trim()) {
      toast.error("Body cannot be empty");
      return;
    }
    updateTemplate({ body: details.body }, "body", "body").then((ok) => {
      if (ok) setEditing((e) => ({ ...e, body: false }));
    });
  };

  const cancelBody = () => {
    setDetails((d) => ({ ...d, body: template.body }));
    setEditing((e) => ({ ...e, body: false }));
  };

  // const handleImageChange = (e) => {
  //   const file = e.target.files?.[0];
  //   setImageError("");
  //   if (!file) {
  //     setImageFile(null);
  //     setImagePreview(null);
  //     return;
  //   }
  //   if (file.type !== "image/png") {
  //     setImageError("Format should be PNG");
  //     e.target.value = "";
  //     setImageFile(null);
  //     setImagePreview(null);
  //     return;
  //   }
  //   if (file.size > MAX_IMAGE_SIZE) {
  //     setImageError("Image should be less than 1MB");
  //     e.target.value = "";
  //     setImageFile(null);
  //     setImagePreview(null);
  //     return;
  //   }
  //   setImageFile(file);
  //   setImagePreview(URL.createObjectURL(file));
  // };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    setImageError("");

    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    setImageValidating(true);
    try {
      await validateImage(file, {
        minWidth: 300,
        minHeight: 200,
        maxFileSize: MAX_IMAGE_SIZE,
        allowedExtensions: ["png"],
        allowedTypes: ["image/png"],
        aspectRatio: 300 / 200,
      });

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageValidating(false);
    } catch (errMessage) {
      setImageError(errMessage);
      e.target.value = "";
      setImageFile(null);
      setImagePreview(null);
      setImageValidating(false);
    }
  };

  const saveImage = () => {
    if (!imageFile) return;
    updateTemplate({ notifImage: imageFile }, "image", "notifImage").then(
      (ok) => {
        if (ok) {
          setImageFile(null);
          setImagePreview(null);
          document.getElementById("Upload-Img").value = "";
        }
      },
    );
  };

  if (!online) {
    return <Error500 message={errMsg} />;
  }

  return (
    <div
      className="wrapper"
      style={{ background: "#f5f6fa", minHeight: "100vh" }}
    >
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .modern-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          padding: 24px 28px;
          margin-bottom: 20px;
          border: 1px solid #eef0f3;
        }
        .field-label {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #8a8f98;
          margin-bottom: 8px;
        }
        .modern-input, .modern-textarea {
          border: 1.5px solid #e4e6eb !important;
          border-radius: 10px !important;
          padding: 10px 14px !important;
          background: #fafbfc !important;
          transition: border-color 0.15s ease, background 0.15s ease;
          width: 100%;
          font-size: 15px;
        }
        .modern-input:focus, .modern-textarea:focus {
          border-color: #6366f1 !important;
          background: #fff !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important;
        }
        .modern-input:disabled, .modern-textarea:disabled {
              border: 1.5px solid #e4e6eb !important;
    border-radius: 10px !important;
    padding: 10px 14px !important;
    background: #fafbfc !important;
    transition: border-color 0.15s ease, background 0.15s ease;
    width: 100%;
    font-size: 15px;
        }
        .icon-btn {
          border: 1.5px solid #e4e6eb;
          border-radius: 8px;
          width: 32px;
          height: 32px;
          min-width: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .icon-btn:hover:not(:disabled) { background: #f5f6fa; border-color: #d1d5db; }
        .icon-btn.success:hover:not(:disabled) { border-color: #22c55e; background: #f0fdf4; }
        .icon-btn.danger:hover:not(:disabled) { border-color: #ef4444; background: #fef2f2; }

        .field-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-end;
        }


        .modern-switch {
          position: relative;
          width: 46px;
          height: 26px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          transition: background 0.2s ease;
          padding: 0;
          flex-shrink: 0;
        }
        .modern-switch::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
          transition: transform 0.2s ease;
        }
        .modern-switch.on { background: #22c55e; }
        .modern-switch.off { background: #d1d5db; }
        .modern-switch.on::after { transform: translateX(20px); }
        .modern-switch:disabled { opacity: 0.6; cursor: not-allowed; }

        .status-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .status-pill {
          font-size: 13px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 999px;
          display: inline-block;
        }
        .status-pill.active { background: #f0fdf4; color: #16a34a; }
        .status-pill.inactive { background: #f4f4f5; color: #71717a; }

        .primary-btn {
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 8px 18px;
          font-weight: 600;
          font-size: 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: background 0.15s ease;
          width: 100%;
          justify-content: center;
        }
        .primary-btn:hover:not(:disabled) { background: #4f46e5; }
        .primary-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        .char-count {
          font-size: 11px;
          color: #a1a1aa;
        }

        .page-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 16px;
          color: #8a8f98;
          gap: 12px;
          text-align: center;
        }

        .image-row {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .thumb-wrap {
          width: 100px;
          height: 66px;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #eef0f3;
          background: #fafbfc;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .image-hint-list {
          font-size: 13px;
          color: #71717a;
          margin: 0;
          padding-left: 18px;
        }

        .page-header-title {
          font-size: 22px;
          font-weight: 700;
          color: #1a1a1a;
        }

        /* ---------- Mobile responsive ---------- */
        @media (max-width: 768px) {
  .field-row {
    flex-direction: column !important;
    align-items: flex-end;
  }
  .field-row .d-flex.flex-column {
    flex-direction: row !important;
  }
    .field-row.title-row {
    align-items: flex-end;
}
}
        @media (max-width: 576px) {
          .modern-card {
            padding: 16px 16px;
            border-radius: 12px;
            margin-bottom: 14px;
          }
          .page-header-title {
            font-size: 18px;
          }
          .field-row.title-row {
            flex-wrap: nowrap;
          }
          .field-row.title-row .position-relative {
            min-width: 0; /* allow input to shrink instead of overflowing */
          }
          .icon-btn {
            width: 28px;
            height: 28px;
            min-width: 28px;
          }
          .modern-input, .modern-textarea {
            font-size: 14px;
            padding: 9px 12px !important;
          }
          .status-row {
            justify-content: space-between;
          }
          .image-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          .thumb-wrap {
            width: 100%;
            max-width: 220px;
            height: 90px;
          }
          .primary-btn {
            width: 100%;
          }
        }

        @media (max-width: 400px) {
          .field-row.title-row {
            flex-wrap: wrap;
          }
          .field-row.title-row .position-relative {
            width: 100%;
          }
          .field-row.title-row > div:last-child {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>

      <section
        className="container py-3 py-md-4 px-3 px-md-4"
        style={{ maxWidth: 720 }}
      >
        <div className="d-flex align-items-center gap-2 mb-4">
          <i
            className="bi bi-arrow-left-circle-fill fs-3 my-auto pointer"
            style={{ cursor: "pointer" }}
            onClick={() => window.history.back()}
          ></i>
          <p className="mb-0 page-header-title">Edit Notification Template</p>
        </div>

        {isLoading || !template ? (
          <div className="page-loader">
            <Spinner size={32} color="#6366f1" />
            <span>Loading template…</span>
          </div>
        ) : (
          <>
            {/* Name */}
            <div className="modern-card">
              <p className="field-label">Template Name</p>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="text"
                  maxLength={50}
                  className="form-control modern-input"
                  readOnly
                  id="Name"
                  name="name"
                  value={template?.name || ""}
                />
                {/* <span
                  className="icon-btn"
                  style={{ color: "#a1a1aa" }}
                  title="Locked"
                >
                  <i className="bi bi-file-earmark-lock2-fill fs-6"></i>
                </span> */}
              </div>
            </div>

            {/* Status toggle — automated templates only */}
            {template?.type === "automated" && (
              <div className="modern-card status-row">
                <div>
                  <p className="field-label mb-1">Status</p>
                  <span
                    className={`status-pill ${
                      template?.status === "Active" ? "active" : "inactive"
                    }`}
                  >
                    {template?.status}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {saving.status && <Spinner size={16} color="#6366f1" />}
                  <button
                    type="button"
                    className={`modern-switch ${
                      template?.status === "Active" ? "on" : "off"
                    }`}
                    disabled={saving.status}
                    onClick={handleStatusToggle}
                    aria-label="Toggle status"
                  />
                </div>
              </div>
            )}

            {/* Title */}
            <div className="modern-card">
              <p className="field-label">Title</p>
              <div className="field-row title-row">
                <div className="position-relative w-100">
                  <input
                    className="form-control modern-input"
                    type="text"
                    value={details.title}
                    onChange={(e) =>
                      setDetails({ ...details, title: e.target.value })
                    }
                    maxLength={50}
                    disabled={!editing.title}
                    id="title"
                    name="title"
                    placeholder="Enter Title"
                  />
                  <p
                    className="char-count"
                    style={{ position: "absolute", right: "12px", top: "12px" }}
                  >
                    {details.title.length} / 50
                  </p>
                </div>
                {editing.title && !editing.body ? (
                  <div className="d-flex gap-2">
                    <button
                      onClick={saveTitle}
                      disabled={saving.title}
                      className="icon-btn success"
                    >
                      {saving.title ? (
                        <Spinner size={16} color="#22c55e" />
                      ) : (
                        <i className="bi bi-check2 fs-5 text-success"></i>
                      )}
                    </button>
                    <button
                      onClick={cancelTitle}
                      disabled={saving.title}
                      className="icon-btn danger"
                    >
                      <i className="bi bi-x fs-5 text-danger"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditing({ body: false, title: true })}
                    className="icon-btn"
                  >
                    <i
                      className="bi bi-pencil-square fs-6"
                      style={{ color: "#6366f1" }}
                    ></i>
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="modern-card">
              <p className="field-label">Body</p>
              <div className="field-row">
                <div className="position-relative w-100">
                  <textarea
                    onChange={(e) =>
                      setDetails({ ...details, body: e.target.value })
                    }
                    maxLength={255}
                    value={details.body}
                    className="form-control modern-textarea"
                    name="description"
                    readOnly={!editing.body}
                    id="description"
                    rows={5}
                    style={{ resize: "none" }}
                  />
                  <p
                    className="char-count"
                    style={{
                      position: "absolute",
                      right: "12px",
                      bottom: "0px",
                    }}
                  >
                    {details.body.length} / 255
                  </p>
                </div>
                {editing.body && !editing.title ? (
                  <div className="d-flex flex-column flex-md-row gap-2">
                    <button
                      onClick={saveBody}
                      disabled={saving.body}
                      className="icon-btn success"
                    >
                      {saving.body ? (
                        <Spinner size={16} color="#22c55e" />
                      ) : (
                        <i className="bi bi-check2 fs-5 text-success"></i>
                      )}
                    </button>
                    <button
                      onClick={cancelBody}
                      disabled={saving.body}
                      className="icon-btn danger"
                    >
                      <i className="bi bi-x fs-5 text-danger"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditing({ body: true, title: false })}
                    className="icon-btn"
                  >
                    <i
                      className="bi bi-pencil-square fs-6"
                      style={{ color: "#6366f1" }}
                    ></i>
                  </button>
                )}
              </div>
            </div>

            {/* Image */}
            <div className="modern-card">
              <p className="field-label">Image</p>
              <input
                className="form-control modern-input"
                type="file"
                id="Upload-Img"
                accept="image/png"
                onChange={handleImageChange}
              />
              {imageValidating && (
                <p
                  className="small mt-2 mb-0 d-flex align-items-center gap-2"
                  style={{ color: "#8a8f98" }}
                >
                  <Spinner size={14} color="#6366f1" /> Checking image…
                </p>
              )}
              {imageError && (
                <p className="text-danger small mt-2 mb-0">{imageError}</p>
              )}

              <div className="mt-3 image-row">
                <div className="thumb-wrap">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="New Template Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : template?.image ? (
                    <img
                      src={app_url + template.image}
                      alt="Template"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <i className="bi bi-image text-muted fs-4"></i>
                  )}
                </div>
                <ul className="image-hint-list">
                  <li>
                    Minimum dimensions: <b>300 × 200 px</b>
                  </li>
                  <li>
                    Aspect ratio must be <b>3:2</b> (e.g. 300 × 200, 600 × 400)
                  </li>
                  <li>
                    File size must be <b>less than 1MB</b>
                  </li>
                  <li>
                    Supported format: <b>PNG</b>
                  </li>
                </ul>
              </div>

              {imageFile && (
                <button
                  className="primary-btn mt-3"
                  onClick={saveImage}
                  disabled={saving.image}
                >
                  {saving.image && <Spinner size={14} color="#fff" />}
                  {saving.image ? "Uploading…" : "Upload Image"}
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default ViewTemplate;
